import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { authService } from '@/services/authService'
import { refreshAccessTokenOnce } from '@/lib/api'
import { getAuthToken, useAuthStore } from '@/stores/authStore'
import { markStaffLogoutIntent } from '@/lib/staffLoginRedirect'
import { customerPostLogoutPath } from '@/lib/customerShopReturn'

/** Warn at most this long before idle logout. */
const IDLE_WARNING_MAX_MS = 5 * 60 * 1000
/** How often the client checks idle / max-session clocks. */
const IDLE_CHECK_MS = 30_000
/**
 * Server idle is extended by authenticated API calls (throttled ~60s) and by a forced
 * touch on /auth/verify. Ping while the user is locally active so UI activity = server activity.
 * Keep this comfortably under typical idle timeouts (min 5 minutes in policy).
 */
const SERVER_HEARTBEAT_MS = 2 * 60 * 1000
/** Ignore high-frequency mouse moves closer than this. */
const MOUSE_THROTTLE_MS = 15_000
/** Refresh this many seconds before JWT exp. */
const REFRESH_SKEW_SECONDS = 60

/** Shared across tabs — sessionStorage caused idle logout from a background tab. */
const LAST_ACTIVITY_KEY = 'oms-last-activity-at'

function touchLocalActivity(): void {
  const now = String(Date.now())
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, now)
  } catch {
    /* private mode / quota */
  }
  try {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now)
  } catch {
    /* ignore */
  }
}

function readLastActivity(): number {
  try {
    const shared = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (shared) {
      const n = Number(shared)
      if (Number.isFinite(n) && n > 0) return n
    }
  } catch {
    /* ignore */
  }
  try {
    const local = sessionStorage.getItem(LAST_ACTIVITY_KEY)
    if (local) {
      const n = Number(local)
      if (Number.isFinite(n) && n > 0) return n
    }
  } catch {
    /* ignore */
  }
  return Date.now()
}

/** Decode JWT exp (seconds) without verifying signature — client scheduling only. */
function readAccessTokenExpMs(token: string | null | undefined): number | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number }
    if (typeof json.exp !== 'number' || !Number.isFinite(json.exp)) return null
    return json.exp * 1000
  } catch {
    return null
  }
}

function idleWarningLeadMs(idleTimeoutMinutes: number): number {
  const idleMs = idleTimeoutMinutes * 60 * 1000
  // For short policies (e.g. 5–15m), warn in the last 20% instead of a fixed 5 minutes.
  return Math.min(IDLE_WARNING_MAX_MS, Math.max(30_000, Math.floor(idleMs * 0.2)))
}

/**
 * Refresh access token before expiry, keep Redis session idle clocks in sync with
 * real UI activity, and warn/sign out when idle or max session limits are hit.
 */
export function useSessionManager() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const expiresIn = useAuthStore((s) => s.expiresIn)
  const token = useAuthStore((s) => s.token)
  const idleTimeoutMinutes = useAuthStore((s) => s.idleTimeoutMinutes)
  const maxSessionExpiresAt = useAuthStore((s) => s.maxSessionExpiresAt)
  const performLogout = useAuthStore((s) => s.performLogout)

  const endStaffSession = (message: string) => {
    markStaffLogoutIntent()
    toast.error(message)
    void performLogout().finally(() => {
      window.location.replace('/auth/staff/login')
    })
  }

  const endCustomerSession = (message: string) => {
    toast.error(message)
    void performLogout().finally(() => {
      window.location.replace(customerPostLogoutPath())
    })
  }

  const endSessionForIdleOrExpiry = (message: string) => {
    if (useAuthStore.getState().user?.is_web_user) {
      endCustomerSession(message)
    } else {
      endStaffSession(message)
    }
  }
  const idleWarnedRef = useRef(false)
  const lastMouseTouchRef = useRef(0)
  const lastHeartbeatRef = useRef(0)

  // Access-token refresh aligned to JWT exp (not just login-time expiresIn).
  useEffect(() => {
    if (!isAuthenticated) return

    touchLocalActivity()

    let cancelled = false
    let timer: number | null = null

    const scheduleNext = () => {
      if (cancelled) return
      if (timer != null) window.clearTimeout(timer)

      const accessToken = useAuthStore.getState().token || getAuthToken()
      const expMs = readAccessTokenExpMs(accessToken)
      const fallbackMs = Math.max(60_000, ((expiresIn ?? 1800) - REFRESH_SKEW_SECONDS) * 1000)
      const delayMs = expMs
        ? Math.max(5_000, expMs - Date.now() - REFRESH_SKEW_SECONDS * 1000)
        : fallbackMs

      timer = window.setTimeout(() => {
        void refreshAccessTokenOnce().finally(() => {
          if (!cancelled) scheduleNext()
        })
      }, delayMs)
    }

    scheduleNext()

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const accessToken = useAuthStore.getState().token || getAuthToken()
      const expMs = readAccessTokenExpMs(accessToken)
      if (expMs && expMs - Date.now() <= REFRESH_SKEW_SECONDS * 1000) {
        void refreshAccessTokenOnce().finally(() => {
          if (!cancelled) scheduleNext()
        })
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isAuthenticated, expiresIn, token])

  // Idle + max-session enforcement, with server heartbeat on real UI activity.
  useEffect(() => {
    if (!isAuthenticated || !idleTimeoutMinutes) return

    const sendHeartbeatIfDue = () => {
      const now = Date.now()
      if (now - lastHeartbeatRef.current < SERVER_HEARTBEAT_MS) return
      lastHeartbeatRef.current = now
      void authService.verifyToken().catch(() => {
        /* 401 handled by interceptor */
      })
    }

    const onActivity = () => {
      touchLocalActivity()
      idleWarnedRef.current = false
      sendHeartbeatIfDue()
    }

    const onMouseMove = () => {
      const now = Date.now()
      if (now - lastMouseTouchRef.current < MOUSE_THROTTLE_MS) return
      lastMouseTouchRef.current = now
      onActivity()
    }

    /** Re-check clocks when returning to the tab; do not treat focus alone as activity. */
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      const idleMs = idleTimeoutMinutes * 60 * 1000
      const inactiveFor = Date.now() - readLastActivity()
      if (inactiveFor >= idleMs) {
        endSessionForIdleOrExpiry('Signed out due to inactivity.')
        return
      }
      // Only extend activity if the user was recently active (e.g. other tab).
      if (inactiveFor < SERVER_HEARTBEAT_MS) {
        sendHeartbeatIfDue()
      }
    }

    /** Another tab recorded activity — keep this tab's idle clock in sync. */
    const onStorage = (event: StorageEvent) => {
      if (event.key !== LAST_ACTIVITY_KEY || event.newValue == null) return
      idleWarnedRef.current = false
    }

    const events: Array<keyof WindowEventMap> = [
      'mousedown',
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ]
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    window.addEventListener('mousemove', onMouseMove, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('storage', onStorage)

    // Align server clock shortly after login / remount if user is present.
    sendHeartbeatIfDue()

    const checkIdle = window.setInterval(() => {
      const maxAt = maxSessionExpiresAt ? new Date(maxSessionExpiresAt).getTime() : null
      if (maxAt && Date.now() >= maxAt) {
        endSessionForIdleOrExpiry('Session expired. Please sign in again.')
        return
      }
      const idleMs = idleTimeoutMinutes * 60 * 1000
      const warnLead = idleWarningLeadMs(idleTimeoutMinutes)
      const lastActivity = readLastActivity()
      const inactiveFor = Date.now() - lastActivity
      if (inactiveFor >= idleMs - warnLead && inactiveFor < idleMs && !idleWarnedRef.current) {
        idleWarnedRef.current = true
        toast('You will be signed out soon due to inactivity', { icon: '⏱️' })
      }
      if (inactiveFor >= idleMs) {
        endSessionForIdleOrExpiry('Signed out due to inactivity.')
      }
    }, IDLE_CHECK_MS)

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity))
      window.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('storage', onStorage)
      window.clearInterval(checkIdle)
    }
  }, [isAuthenticated, idleTimeoutMinutes, maxSessionExpiresAt, performLogout])
}
